import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function BillingSuccess() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let attempt = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch { /* swallow & retry */ }
      attempt += 1;
      if (attempt >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-zinc-50 p-6">
      <div className="card-elev max-w-lg w-full text-center" data-testid="billing-success">
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: "var(--gold)" }} />
            <h1 className="mt-4 text-2xl font-bold" data-testid="billing-success-title">You&apos;re all set.</h1>
            <p className="mt-2 text-zinc-600">Your subscription is active. We&apos;ve linked it to your clinic.</p>
            {info?.amount_total != null && (
              <p className="text-sm text-zinc-500 mt-1">Paid: ${(info.amount_total / 100).toLocaleString()} {info.currency?.toUpperCase()}</p>
            )}
            <Link to="/app/clinic" className="btn-gold mt-6 inline-flex" data-testid="billing-back-dashboard">Go to dashboard</Link>
          </>
        )}
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-zinc-400" />
            <h1 className="mt-4 text-xl font-bold">Confirming your payment&hellip;</h1>
            <p className="mt-2 text-zinc-600 text-sm">This usually takes a few seconds.</p>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "missing") && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="mt-4 text-xl font-bold">We couldn&apos;t confirm your payment.</h1>
            <p className="mt-2 text-zinc-600 text-sm">If you were charged, please refresh this page or contact support.</p>
            <Link to="/pricing" className="btn-outline mt-6 inline-flex" data-testid="billing-retry">Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
